import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { subscribeApplications, subscribeOrders } from "../../services/adminService";
import { useToast } from "./Toast";

const InboxContext = createContext({
  apps: [],
  orders: [],
  ready: { apps: false, orders: false },
  errors: { apps: "", orders: "" },
  newApps: 0,
  newOrders: 0,
});

export const useInbox = () => useContext(InboxContext);

export function InboxProvider({ children }) {
  const toast = useToast();
  const [apps, setApps] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ready, setReady] = useState({ apps: false, orders: false });
  const [errors, setErrors] = useState({ apps: "", orders: "" });
  const first = useRef({ apps: true, orders: true });

  useEffect(() => {
    const handle = (key, setter, label, nameOf) => (rows, addedIds) => {
      setter(rows);
      setReady((r) => ({ ...r, [key]: true }));
      setErrors((e) => ({ ...e, [key]: "" }));
      if (first.current[key]) {
        first.current[key] = false; 
        return;
      }
      addedIds.forEach((id) => {
        const row = rows.find((r) => r.id === id);
        if (row) toast.show(`${label}: ${nameOf(row)}`);
      });
    };
    const fail = (key) => (err) => {
      console.error(`Live ${key} updates failed`, err);
      setErrors((e) => ({ ...e, [key]: "Could not load live updates. Check your connection and Firestore rules." }));
      setReady((r) => ({ ...r, [key]: true }));
    };

    const stopApps = subscribeApplications(
      handle("apps", setApps, "New membership application", (r) => r.fullName),
      fail("apps")
    );
    const stopOrders = subscribeOrders(
      handle("orders", setOrders, "New shop order", (r) => r.customer),
      fail("orders")
    );
    return () => {
      stopApps();
      stopOrders();
    };
  }, [toast]);

  const value = useMemo(
    () => ({
      apps,
      orders,
      ready,
      errors,
      newApps: apps.filter((a) => !a.registered && !a.dismissed).length,
      newOrders: orders.filter((o) => o.status === "new").length,
    }),
    [apps, orders, ready, errors]
  );

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
}
